import { cn } from "../utils/cn";

export type Feedback = "positive" | "negative" | undefined;

interface PropertyPalFeedbackButtonsProps {
  feedback?: Feedback;
  onFeedbackChange?: (feedback: Feedback) => void;
}

const baseButtonStyles =
  "size-5 flex items-center justify-center rounded-sm outline-none focus-visible:outline-1 focus-visible:outline-loch";

const iconBaseStyles = "fa-sharp fa-regular text-[16px]";

const thumbsColours = {
  default: "text-neutral-400 hover:text-neutral-500",
  otherSelected: "text-neutral-300 hover:text-neutral-400",
  upSelected: "text-loch hover:text-loch-700",
  downSelected: "text-brick hover:text-brick-700",
};

const getThumbsUpColor = (feedback: Feedback) => {
  if (feedback === "positive") return thumbsColours.upSelected;
  if (feedback === "negative") return thumbsColours.otherSelected;
  return thumbsColours.default;
};

const getThumbsDownColor = (feedback: Feedback) => {
  if (feedback === "negative") return thumbsColours.downSelected;
  if (feedback === "positive") return thumbsColours.otherSelected;
  return thumbsColours.default;
};

export const PropertyPalFeedbackButtons = ({ feedback, onFeedbackChange }: PropertyPalFeedbackButtonsProps) => {
  const isPositive = feedback === "positive";
  const isNegative = feedback === "negative";

  const handlePositiveClick = () => {
    onFeedbackChange?.(isPositive ? undefined : "positive");
  };

  const handleNegativeClick = () => {
    onFeedbackChange?.(isNegative ? undefined : "negative");
  };

  return (
    <div role="group" aria-label="Rate this response" className="flex gap-2 items-start">
      <button
        type="button"
        className={cn(baseButtonStyles, getThumbsUpColor(feedback))}
        aria-label="Rate positively"
        aria-pressed={isPositive}
        onClick={handlePositiveClick}
      >
        <i className={cn(iconBaseStyles, "fa-thumbs-up")} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={cn(baseButtonStyles, getThumbsDownColor(feedback))}
        aria-label="Rate negatively"
        aria-pressed={isNegative}
        onClick={handleNegativeClick}
      >
        <i className={cn(iconBaseStyles, "fa-thumbs-down")} aria-hidden="true" />
      </button>
    </div>
  );
};
